import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { CASE_STUDIES_DATA } from '../mock/caseStudiesData';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  ChevronRight,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Truck,
  ShieldCheck,
  Clock,
  Package,
  CheckCircle2,
  AlertTriangle,
  Building2,
  MapPin,
  Calendar,
  Gift,
  HelpCircle,
  Award,
  Layers,
  Check,
} from 'lucide-react';

const CaseStudyDetail = ({ forcedSlug }) => {
  const params = useParams();
  const rawSlug = forcedSlug || params.slug;

  // Resolve case study by direct slug or legacy alias
  let study = CASE_STUDIES_DATA[rawSlug];
  if (!study && rawSlug) {
    study = Object.values(CASE_STUDIES_DATA).find(
      (item) => item.legacyAliases && item.legacyAliases.includes(rawSlug)
    );
  }

  if (!study) {
    return <Navigate to="/case-studies" replace />;
  }

  // 1. Breadcrumbs Schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://wrapyourwish.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Case Studies',
        item: 'https://wrapyourwish.com/case-studies',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: study.title,
        item: study.seo.canonicalUrl,
      },
    ],
  };

  // 2. Article / Case Study Schema
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: study.title,
    description: study.seo.metaDescription,
    image: study.seo.ogImage,
    datePublished: '2026-01-15T08:00:00+05:30',
    dateModified: '2026-09-15T12:00:00+05:30',
    author: {
      '@type': 'Organization',
      name: 'Wrap Your Wish',
      url: 'https://wrapyourwish.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Wrap Your Wish',
      logo: {
        '@type': 'ImageObject',
        url: 'https://wrapyourwish.com/logo_new.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': study.seo.canonicalUrl,
    },
  };

  // 3. FAQ Schema
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: study.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.a,
      },
    })),
  };

  // Related studies
  const relatedStudies = (study.relatedSlugs || [])
    .map((s) => CASE_STUDIES_DATA[s])
    .filter(Boolean);

  return (
    <div className="bg-[#FAF6F1] min-h-screen text-[#2C2416]">
      <SEO
        title={study.seo.title}
        description={study.seo.metaDescription}
        keywords={study.seo.keywords}
        canonicalUrl={study.seo.canonicalUrl}
        ogImage={study.seo.ogImage}
        ogType="article"
        schemas={[breadcrumbSchema, articleSchema, faqSchema]}
      />

      {/* Hero Header */}
      <section className="bg-gradient-to-b from-[#F4E7DA] via-[#FAF6F1] to-[#FAF6F1] border-b border-[#E9DFD3] pt-10 pb-16 md:pb-20">
        <div className="max-w-6xl mx-auto px-4">
          
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumbs" className="flex items-center gap-2 text-xs text-[#7A5C4C] mb-6 flex-wrap">
            <Link to="/" className="hover:text-[#C58B76] transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-[#C58B76]/60" />
            <Link to="/case-studies" className="hover:text-[#C58B76] transition-colors">Client Stories</Link>
            <ChevronRight className="w-3.5 h-3.5 text-[#C58B76]/60" />
            <span className="text-[#2C2416] font-medium truncate max-w-[280px] sm:max-w-md">{study.badge}</span>
          </nav>

          {/* Badge & Meta */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase bg-[#C58B76]/15 text-[#8F4A32] border border-[#C58B76]/30">
              <Award className="w-3.5 h-3.5 text-[#C58B76]" />
              {study.badge}
            </span>
            <span className="text-xs tracking-wider uppercase text-[#7A5C4C] font-medium">
              Case Study &bull; {study.category}
            </span>
          </div>

          {/* H1 Heading */}
          <h1 className="font-serif-display text-3xl sm:text-4xl md:text-5xl lg:text-5xl text-[#2C2416] leading-[1.15] mb-6 max-w-4xl">
            {study.title}
          </h1>

          <p className="text-base sm:text-lg text-[#5B4A3A] leading-relaxed max-w-3xl mb-8">
            {study.subtitle}
          </p>

          {/* Client At-A-Glance Bar */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <div className="bg-white rounded-2xl p-4 sm:p-5 border border-[#EFE3D2] shadow-sm flex items-start gap-3.5 min-w-0 hover:border-[#C58B76]/40 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-[#FAF6F1] flex items-center justify-center text-[#C58B76] shrink-0 border border-[#E9DFD3]">
                <Building2 className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-wider text-[#7A5C4C] font-semibold mb-0.5">Industry</div>
                <div className="text-xs sm:text-sm font-semibold text-[#2C2416] leading-snug break-words">
                  {study.clientIndustry}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4 sm:p-5 border border-[#EFE3D2] shadow-sm flex items-start gap-3.5 min-w-0 hover:border-[#C58B76]/40 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-[#FAF6F1] flex items-center justify-center text-[#C58B76] shrink-0 border border-[#E9DFD3]">
                <MapPin className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-wider text-[#7A5C4C] font-semibold mb-0.5">Location</div>
                <div className="text-xs sm:text-sm font-semibold text-[#2C2416] leading-snug break-words">
                  {study.clientLocation}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4 sm:p-5 border border-[#EFE3D2] shadow-sm flex items-start gap-3.5 min-w-0 hover:border-[#C58B76]/40 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-[#FAF6F1] flex items-center justify-center text-[#C58B76] shrink-0 border border-[#E9DFD3]">
                <Clock className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-wider text-[#7A5C4C] font-semibold mb-0.5">Timeline</div>
                <div className="text-xs sm:text-sm font-semibold text-[#2C2416] leading-snug break-words">
                  {study.turnaroundTime}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4 sm:p-5 border border-[#EFE3D2] shadow-sm flex items-start gap-3.5 min-w-0 hover:border-[#C58B76]/40 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-[#FAF6F1] flex items-center justify-center text-[#C58B76] shrink-0 border border-[#E9DFD3]">
                <Package className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-wider text-[#7A5C4C] font-semibold mb-0.5">Volume</div>
                <div className="text-xs sm:text-sm font-semibold text-[#2C2416] leading-snug break-words">
                  {study.hamperVolume}
                </div>
              </div>
            </div>
          </div>

          {/* Testimonial Quote Spotlight */}
          <div className="relative bg-[#2C2416] text-[#FAF6F1] rounded-2xl p-6 sm:p-8 md:p-10 shadow-lg border border-[#3E3224]">
            <div className="text-5xl md:text-6xl text-[#C58B76] font-serif leading-none select-none mb-2">“</div>
            <p className="font-serif italic text-lg sm:text-xl md:text-2xl text-[#FAF6F1] leading-relaxed mb-6">
              {study.quote}
            </p>
            <div className="flex items-center justify-between flex-wrap gap-4 border-t border-[#4A3B2C] pt-4">
              <div>
                <div className="font-semibold text-sm text-[#F4D5C5]">{study.quoteAuthor}</div>
                <div className="text-xs text-[#D9CBB4]">{study.quoteCompany}</div>
              </div>
              <a
                href="#enquire"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs uppercase tracking-widest font-semibold bg-[#F4D5C5] text-[#2C2416] hover:bg-white transition-colors"
              >
                Start Your Project <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

        </div>
      </section>

      {/* Metrics Bar */}
      <section className="py-12 bg-white border-b border-[#E9DFD3]">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {study.metrics.map((m, idx) => (
              <div key={idx} className="p-5 rounded-2xl bg-[#FAF6F1] border border-[#EFE3D2] flex flex-col justify-between min-w-0 shadow-2xs">
                <div>
                  <div className="font-serif-display text-3xl sm:text-4xl text-[#C58B76] font-bold mb-1">
                    {m.value}
                  </div>
                  <div className="font-medium text-xs sm:text-sm text-[#2C2416] leading-snug mb-2">
                    {m.label}
                  </div>
                </div>
                <div className="text-[11px] text-[#7A5C4C] leading-tight border-t border-[#EFE3D2] pt-2">
                  {m.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deep Dive: The Challenge & The Solution */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            
            {/* The Challenge Column */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 md:p-10 border border-[#E9DFD3] shadow-sm">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#B45309] font-semibold mb-3">
                <AlertTriangle className="w-4 h-4 text-[#D97706]" />
                The Operational Challenge
              </div>
              <h2 className="font-serif-display text-2xl sm:text-3xl text-[#2C2416] mb-4 leading-tight">
                {study.challenge.heading}
              </h2>
              <p className="text-sm sm:text-base text-[#5B4A3A] leading-relaxed mb-6">
                {study.challenge.intro}
              </p>

              <div className="space-y-4">
                {study.challenge.points.map((pt, i) => (
                  <div key={i} className="p-4 rounded-xl bg-[#FFFBEB] border border-[#FDE68A]">
                    <div className="font-semibold text-xs sm:text-sm text-[#92400E] mb-1 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-[#D97706]" />
                      {pt.title}
                    </div>
                    <p className="text-xs sm:text-sm text-[#78350F] leading-relaxed pl-4">
                      {pt.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* The Solution Column */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 md:p-10 border border-[#E9DFD3] shadow-sm">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#15803D] font-semibold mb-3">
                <CheckCircle2 className="w-4 h-4 text-[#16A34A]" />
                The Wrap Your Wish Strategy
              </div>
              <h2 className="font-serif-display text-2xl sm:text-3xl text-[#2C2416] mb-4 leading-tight">
                {study.solution.heading}
              </h2>
              <p className="text-sm sm:text-base text-[#5B4A3A] leading-relaxed mb-6">
                {study.solution.intro}
              </p>

              <div className="space-y-4">
                {study.solution.points.map((pt, i) => (
                  <div key={i} className="p-4 rounded-xl bg-[#F0FDF4] border border-[#BBF7D0]">
                    <div className="font-semibold text-xs sm:text-sm text-[#166534] mb-1 flex items-center gap-2">
                      <Check className="w-4 h-4 text-[#16A34A]" />
                      {pt.title}
                    </div>
                    <p className="text-xs sm:text-sm text-[#14532D] leading-relaxed pl-6">
                      {pt.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Execution Workflow / Timeline */}
      <section className="py-16 bg-[#F4E7DA] border-y border-[#E9DFD3]">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs uppercase tracking-[0.24em] text-[#C58B76] mb-2 font-semibold">
              Agile Execution Roadmap
            </div>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#2C2416]">
              How the Project Was Delivered
            </h2>
            <p className="text-sm text-[#5B4A3A] mt-2">
              From briefing to final doorstep dispatch, managed seamlessly end-to-end.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {study.timeline.map((step, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl p-6 border border-[#E4D6C1] shadow-sm relative flex flex-col justify-between min-w-0"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-mono font-bold text-[#C58B76] px-2.5 py-1 rounded bg-[#FAF6F1] border border-[#E9DFD3]">
                      {step.step}
                    </span>
                    <span className="text-xs uppercase tracking-wider font-semibold text-[#7A5C4C]">
                      {step.day}
                    </span>
                  </div>
                  <h3 className="font-serif-display text-lg text-[#2C2416] mb-2 leading-snug">
                    {step.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#5B4A3A] leading-relaxed">
                    {step.desc}
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-[#F0E7DA] flex items-center gap-1.5 text-[11px] font-medium text-[#C58B76]">
                  <Check className="w-3 h-3" /> Step Verified
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inside the Hamper: Curated Elements Showcase */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between md:items-end gap-4 mb-12">
            <div>
              <div className="text-xs uppercase tracking-[0.24em] text-[#C58B76] mb-2 font-semibold">
                Curated Elements
              </div>
              <h2 className="font-serif-display text-3xl sm:text-4xl text-[#2C2416]">
                What Was Inside the Hampers
              </h2>
            </div>
            <p className="text-sm text-[#5B4A3A] max-w-md">
              Every single component was selected for premium aesthetic balance, brand resonance, and sensory delight.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {study.curatedItems.map((item, idx) => (
              <div
                key={idx}
                className="group bg-[#FAF6F1] rounded-2xl overflow-hidden border border-[#EFE3D2] flex flex-col justify-between hover:shadow-md transition-shadow min-w-0"
              >
                <div className="aspect-square overflow-hidden bg-[#F0E7DA]">
                  <img
                    src={item.image}
                    alt={item.name}
                    width="400"
                    height="400"
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5 flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="font-serif-display text-base sm:text-lg text-[#2C2416] mb-2 leading-snug">
                      {item.name}
                    </h3>
                    <p className="text-xs text-[#5B4A3A] leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-[#E9DFD3] flex items-center gap-1 text-[11px] text-[#C58B76] font-medium">
                    <Sparkles className="w-3 h-3" /> Custom Branded Element
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Results & Best Practice Guide */}
      <section className="py-16 md:py-24 bg-[#FAF6F1] border-t border-[#E9DFD3]">
        <div className="max-w-6xl mx-auto px-4">
          
          {/* Results Block */}
          <div className="bg-[#2C2416] text-[#FAF6F1] rounded-3xl p-8 sm:p-10 md:p-14 shadow-xl border border-[#3E3224] mb-16">
            <div className="max-w-3xl">
              <div className="text-xs uppercase tracking-[0.24em] text-[#F4D5C5] mb-3 font-semibold">
                Business Outcomes
              </div>
              <h2 className="font-serif-display text-3xl sm:text-4xl text-[#FAF6F1] mb-5 leading-tight">
                {study.results.heading}
              </h2>
              <p className="text-base text-[#D9CBB4] leading-relaxed mb-8">
                {study.results.summary}
              </p>

              <div className="space-y-3">
                {study.results.highlights.map((hl, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm text-[#FAF6F1]">
                    <div className="w-5 h-5 rounded-full bg-[#C58B76]/30 flex items-center justify-center shrink-0 mt-0.5 text-[#F4D5C5]">
                      <Check className="w-3.5 h-3.5" />
                    </div>
                    <span>{hl}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SEO Best Practice Editorial Box */}
          <div className="bg-white rounded-2xl p-8 md:p-10 border border-[#E9DFD3] shadow-sm mb-16">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#C58B76] font-semibold mb-3">
              <Layers className="w-4 h-4" /> Strategic Industry Insights
            </div>
            <h3 className="font-serif-display text-2xl sm:text-3xl text-[#2C2416] mb-4">
              {study.bestPractices.title}
            </h3>
            <p className="text-sm sm:text-base text-[#5B4A3A] leading-relaxed">
              {study.bestPractices.content}
            </p>
          </div>

          {/* FAQ Accordion Section */}
          <div className="max-w-3xl mx-auto mb-16">
            <div className="text-center mb-8">
              <div className="text-xs uppercase tracking-[0.24em] text-[#C58B76] mb-2 font-semibold">
                Frequently Asked Questions
              </div>
              <h2 className="font-serif-display text-2xl sm:text-3xl text-[#2C2416]">
                Everything You Need to Know
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-3">
              {study.faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white rounded-xl border border-[#E9DFD3] px-6 shadow-sm overflow-hidden"
                >
                  <AccordionTrigger className="text-left font-serif text-base sm:text-lg text-[#2C2416] hover:no-underline hover:text-[#C58B76] py-4">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-xs sm:text-sm text-[#5B4A3A] leading-relaxed pb-4 pt-1 border-t border-[#F0E7DA]">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Read Next / Related Stories */}
          {relatedStudies.length > 0 && (
            <div className="border-t border-[#E9DFD3] pt-14">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <div className="text-xs uppercase tracking-[0.24em] text-[#C58B76] mb-1 font-semibold">
                    More Client Success Stories
                  </div>
                  <h3 className="font-serif-display text-2xl sm:text-3xl text-[#2C2416]">
                    Explore Other Case Studies
                  </h3>
                </div>
                <Link
                  to="/case-studies"
                  className="text-xs uppercase tracking-widest text-[#7A5C4C] hover:text-[#C58B76] font-semibold inline-flex items-center gap-1"
                >
                  View All Stories <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {relatedStudies.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/case-studies/${rel.slug}`}
                    className="group bg-white rounded-2xl p-6 sm:p-8 border border-[#E9DFD3] shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
                  >
                    <div>
                      <div className="text-xs uppercase tracking-[0.2em] text-[#C58B76] font-semibold mb-3">
                        {rel.badge}
                      </div>
                      <h4 className="font-serif-display text-xl text-[#2C2416] group-hover:text-[#C58B76] transition-colors leading-snug mb-3">
                        {rel.title}
                      </h4>
                      <p className="text-xs text-[#5B4A3A] line-clamp-3 leading-relaxed mb-6">
                        "{rel.quote}"
                      </p>
                    </div>
                    <div className="flex items-center justify-between border-t border-[#F0E7DA] pt-4 text-xs font-semibold text-[#C58B76] uppercase tracking-wider">
                      <span>Read Full Story</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

        </div>
      </section>

      {/* Embedded Contact / Brief Form */}
      <ContactForm source={`case-study-${study.slug}`} />
    </div>
  );
};

export default CaseStudyDetail;
